/**
 * Studio start-choice bridge.
 *
 * The studio's start choices (start with my previous check / start
 * blank / my saved designs / browse patterns) are menu items in the
 * app shell — the hamburger drawer on mobile, the sidebar on desktop
 * (see StudioStartMenu) — but the actions themselves live in
 * CheckBuilder: the design-history hook owns the previous check, and
 * the designs/patterns entries open CheckBuilder's own panels.
 *
 * The shell and the studio already talk this way for Send Check
 * delivery (`oc:deliver`); this bridge is the same pattern for the
 * start offer, so neither side imports the other:
 *
 *  - CheckBuilder publishes whether a previous check is on offer
 *    whenever that changes (and clears the offer on unmount). The
 *    last value is cached here, so a menu mounting after the studio
 *    still sees the current offer.
 *  - A menu click requests an action; CheckBuilder is the single
 *    listener and runs the same handlers the old stacked start bar
 *    (removed 2026-11-04, Princewill: the choices belong in the
 *    hamburger menu, not stacked over the design) called.
 */

export type StudioStartAction = 'previous' | 'blank' | 'designs' | 'patterns';

const OFFER_EVENT = 'oc:start-offer';
const ACTION_EVENT = 'oc:start-action';

let lastHasPrevious = false;

export function publishStudioStartOffer(hasPrevious: boolean): void {
    lastHasPrevious = hasPrevious;
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(OFFER_EVENT, { detail: { hasPrevious } }));
    }
}

export function currentStudioStartOffer(): boolean {
    return lastHasPrevious;
}

export function subscribeStudioStartOffer(cb: (hasPrevious: boolean) => void): () => void {
    if (typeof window === 'undefined') return () => { /* no window, no offers */ };
    const onEvent = (e: Event) => cb(Boolean((e as CustomEvent).detail?.hasPrevious));
    window.addEventListener(OFFER_EVENT, onEvent);
    return () => window.removeEventListener(OFFER_EVENT, onEvent);
}

export function requestStudioStartAction(action: StudioStartAction): void {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(ACTION_EVENT, { detail: { action } }));
    }
}

export function onStudioStartAction(cb: (action: StudioStartAction) => void): () => void {
    if (typeof window === 'undefined') return () => { /* no window, no actions */ };
    const onEvent = (e: Event) => {
        const action = (e as CustomEvent).detail?.action;
        if (action === 'previous' || action === 'blank' || action === 'designs' || action === 'patterns') {
            cb(action);
        }
    };
    window.addEventListener(ACTION_EVENT, onEvent);
    return () => window.removeEventListener(ACTION_EVENT, onEvent);
}
